import { useEffect, useRef } from 'react';

// App.js turns a socket notification whose `metadata.action` asks for one
// (`api_call` / `notification_and_call`) into this window event, debounced 2s —
// one signal every screen showing document state can refresh itself from.
export const SOCKET_REFETCH_EVENT = 'triggerApiFromSocket';

/**
 * Re-reads what the screen is showing when the socket says something changed
 * underneath it.
 *
 * The listener is registered ONCE on mount, so the callback handed in would
 * otherwise be the mount-time closure forever — reading the document id, the
 * loading flag and the unsaved-edits flag as they were when the screen opened,
 * not as they are when the event fires. It is held in a ref refreshed on every
 * render and read at fire time, the same shape homePage.jsx uses for its own
 * listener (`intervalCallRef`).
 *
 * The callback is handed the event's `detail` — `{documentIds}`, the ids every
 * notification in the debounce window named, or `null` when one of them named
 * nothing. Whether a refetch is a good idea is the caller's decision, not this
 * hook's: it only guarantees that the CURRENT callback is the one called, with
 * what the notification carried, and that the listener leaves with the component.
 */
function useSocketRefetch(onRefetch) {
  const handlerRef = useRef(onRefetch);
  useEffect(() => {
    handlerRef.current = onRefetch;
  });

  useEffect(() => {
    const handle = (event) => handlerRef.current?.(event?.detail);
    window.addEventListener(SOCKET_REFETCH_EVENT, handle);
    return () => window.removeEventListener(SOCKET_REFETCH_EVENT, handle);
  }, []);
}

export default useSocketRefetch;
